import { ContractRequestUpdateDTO } from 'models/ContractRequestUpdateDTO'
import formatter from 'utils/formatter'
import { StepsFormProps } from '../interface'

export const mountUpdateContract = (
    data: StepsFormProps
): ContractRequestUpdateDTO => {
    const {
        contract,
        complementary,
        financing,
        consortium,
        creditor,
        debtor,
        terceiroGarantidor,
        anexos
    } = data

    const percentuais = [
        'valorTaxaJurosAno',
        'valorTaxaJurosMes',
        'valorTaxaMora',
        'valorTaxaMulta'
    ]

    const currencies = [
        'valorIOF',
        'valorComissao',
        'valorParcela',
        'valorTaxaContrato',
        'valorTotalDivida'
    ]

    const hasValue = (value?: any) =>
        value !== undefined && value !== null && String(value).trim() !== ''

    const hasThirdGuarantor = (value?: any) => {
        if (!value) return false

        return (
            hasValue(value.nome) ||
            hasValue(value.email) ||
            hasValue(value.tipoDocumento) ||
            hasValue(value.documento) ||
            hasValue(value.telefone) ||
            hasValue(value.endereco?.cep) ||
            hasValue(value.endereco?.logradouro) ||
            hasValue(value.endereco?.numero) ||
            hasValue(value.endereco?.bairro) ||
            hasValue(value.endereco?.municipio) ||
            hasValue(value.endereco?.uf) ||
            hasValue(value.endereco?.complemento)
        )
    }

    const mountTerceiroGarantidor = (value?: any) => {
        if (!hasThirdGuarantor(value)) return undefined

        const municipio = value.endereco?.municipio || ''
        const [codigoMunicipio, nomeMunicipio] = municipio.includes('|')
            ? municipio.split('|')
            : ['', municipio]

        return {
            nome: value.nome,
            email: value.email,
            tipoDocumento: value.tipoDocumento,
            documento: formatter.cleanFormatter(value.documento),
            telefone: formatter.cleanFormatter(value.telefone),
            endereco: {
                ...value.endereco,
                cep: formatter.cleanFormatter(value.endereco?.cep),
                municipio: nomeMunicipio,
                codigoMunicipio: codigoMunicipio ? +codigoMunicipio : undefined
            }
        }
    }

    const indicador = [
        'identificadorTaxaMora',
        'identificadorTaxaMulta',
        'identificadorComissao',
        'indicacaoPenalidade'
    ]

    const deniedFields = [
        'chassi',
        'renavam',
        'ufGravame',
        'especie',
        'uf',
        'placa',
        'anoFabricacao',
        'anoModelo',
        'identificacaoRemarcacao',
        'principalRecebedorPagamento',
        'tipoDocumentoRecebedor',
        'documentoPrincipalRecebedorPagamento',
        'tipoDocumentoVendedor',
        'documentoVendedor',
        'comentarios',
        'dataContrato',
        'numeroContrato',
        'numeroGravame',
        'tipoRestricao',
        'ufContrato',
        'municipioContrato',
        'cnpj',
        'razaoSocial',
        'nome',
        'tipoDocumento',
        'documento',
        'numeroDudaPago'
    ]

    return {
        ...Object.assign(
            {},
            ...Object.entries(complementary).flatMap(([name, value]) => {
                if (currencies.includes(name)) {
                    return {
                        [name]: value ? +value.replace(/\D/g, '') / 100 : value
                    }
                } else if (percentuais.includes(name)) {
                    return {
                        [name]: value ? +value.replace(/\D/g, '') / 1000 : value
                    }
                } else if (deniedFields.includes(name)) {
                    return {
                        [name]: null
                    }
                } else if (indicador.includes(name)) {
                    let boolValue = false

                    if (value.toUpperCase() === 'SIM') {
                        boolValue = true
                    } else if (value.toUpperCase() === 'NAO') {
                        boolValue = false
                    }

                    return {
                        [name]: boolValue
                    }
                } else {
                    return { [name]: value }
                }
            })
        ),

        ...Object.assign(
            {},
            ...Object.entries(financing).flatMap(([name, value]) => {
                if (
                    [
                        'dataLiberacaoCredito',
                        'municipioLiberacaoCredito',
                        'ufLiberacaoCredito'
                    ].includes(name)
                ) {
                    return {
                        liberacaoCredito: {
                            dataLiberacaoCredito:
                                financing.dataLiberacaoCredito,
                            ufLiberacaoCredito: financing.ufLiberacaoCredito,
                            municipioLiberacaoCredito: {
                                codigo: parseInt(
                                    financing.municipioLiberacaoCredito.split(
                                        '|'
                                    )[0],
                                    10
                                ),
                                municipio:
                                    financing.municipioLiberacaoCredito.split(
                                        '|'
                                    )[1]
                            }
                        }
                    }
                } else if (currencies.includes(name)) {
                    return {
                        [name]: value ? +value.replace(/\D/g, '') / 100 : value
                    }
                } else if (deniedFields.includes(name)) {
                    return {
                        [name]: null
                    }
                } else {
                    return { [name]: value }
                }
            })
        ),
        tipoAditivo: contract.tipoAditivo || null,
        indices: complementary.indices,
        penalidade: complementary.penalidade,
        comentarios: complementary.comentarios,
        vencimentoPrimeiraParcela: financing.vencimentoPrimeiraParcela,
        vencimentoUltimaParcela: financing.vencimentoUltimaParcela,
        cotaConsorcio: consortium.cotaConsorcio,
        numeroGrupo: consortium.numeroGrupo,
        numeroDudaPago: contract.numeroDudaPago,
        quantidadeMeses: contract.quantidadeMeses,
        tipoRestricao: contract.tipoRestricao,
        ...anexos,
        origem: 'TELA',
        credor: {
            telefone: formatter.cleanFormatter(creditor.telefone),
            email: creditor.email,
            endereco: {
                ...creditor.endereco,
                cep: formatter.cleanFormatter(creditor.endereco.cep),
                municipio: creditor.endereco.municipio.split('|')[1],
                codigoMunicipio: +creditor.endereco.municipio.split('|')[0]
            }
        },
        devedor: {
            nome: debtor.nome,
            documento: formatter.cleanFormatter(debtor.documento),
            tipoDocumento: debtor.tipoDocumento,
            email: debtor.email,
            telefone: formatter.cleanFormatter(debtor.telefone),
            endereco: {
                ...debtor.endereco,
                cep: formatter.cleanFormatter(debtor.endereco.cep),
                municipio: debtor.endereco.municipio.split('|')[1],
                codigoMunicipio: +debtor.endereco.municipio.split('|')[0]
            }
        },
        ...(mountTerceiroGarantidor(terceiroGarantidor)
            ? { terceiroGarantidor: mountTerceiroGarantidor(terceiroGarantidor) }
            : {})
    }
}
